import { useEffect } from 'react'
import { Mail, Phone, Eye, Gift, X as CloseIcon } from 'lucide-react'
import { usePOSStore, isReceiptPartiallyPaid } from '@/stores/posStore'
import { useCartStore } from '@/stores/cartStore'
import { useCustomerSpringbig } from '@/api/customers'
import { usePOSConfig } from '@/api/config'
import { formatPrice } from '@/lib/utils'
import type { Customer } from '@/types'
import {
  BlockedBadge,
  SPRINGBIG_PERCENT_TYPE,
  computePercentDollar,
  type CustomerModalTabId,
} from '@/components/customers'

export interface OpenCustomerDetailsOptions {
  initialTab?: CustomerModalTabId
}

export interface CustomerHeaderProps {
  customer: Customer
  onChangeCustomer: () => void
  onOpenDetails?: (customer: Customer, options?: OpenCustomerDetailsOptions) => void
}

export function CustomerHeader({ customer, onChangeCustomer, onOpenDetails }: CustomerHeaderProps) {
  const pendingReward = usePOSStore((s) => s.pendingReward)
  const pendingOffer = usePOSStore((s) => s.pendingOffer)
  const setPendingReward = usePOSStore((s) => s.setPendingReward)
  const setPendingOffer = usePOSStore((s) => s.setPendingOffer)
  const serverTotals = usePOSStore((s) => s.serverTotals)
  const isRefundMode = usePOSStore((s) => s.isRefundMode)
  // Lock customer changes on a partially-paid receipt. Swapping a
  // customer mid-tender re-prices line items at the new customer's
  // tier, which shifts the cart total against a BE that already has
  // payment JTs riding on the previous shape. useSelectCustomer
  // (the hook the Change button funnels through) carries the same
  // guard with a toast; we disable the button here so the cashier
  // sees up-front it isn't actionable.
  const customerLocked = isReceiptPartiallyPaid(serverTotals, isRefundMode)
  // Live cart totals — drive the percent-redemption dollar estimate on
  // the staged chip. Prefer preview when present (current cart state),
  // fall back to posStore.serverTotals when preview is missing (refund/
  // unhold flows skip preview, refresh-mid-checkout sees an empty
  // preview). Mirrors the precedence CheckoutModal uses so both
  // surfaces show the same dollar value.
  const previewTotals = useCartStore((s) => s.preview?.totals ?? null)
  const { data: posConfig } = usePOSConfig()
  const cartPercentBase = posConfig?.posApplyRewardsDiscountsOnFinalTotal
    ? Number(previewTotals?.total ?? serverTotals?.total ?? 0)
    : Number(previewTotals?.subtotal ?? serverTotals?.subtotal ?? 0)
  const rewardChipAmount = stagedDisplayDollar(pendingReward, cartPercentBase)
  const offerChipAmount = stagedDisplayDollar(pendingOffer, cartPercentBase)
  // Fires the springbig query for the active customer so we can flash a
  // reward icon when SpringBig has affordable rewards waiting. The hook
  // has 30s staleTime + drawer-open is the natural refresh trigger, so
  // attaching here only adds one network call per fresh customer attach.
  const { data: springbig } = useCustomerSpringbig(customer.id)
  const hasReward = !!(
    springbig?.customer_enrolled
    && (springbig.rewards?.length ?? 0) > 0
  )

  // Mirror loyalty state into the cart so the CFD picks it up over the same
  // broadcast pipeline (same pattern as CheckoutModal's partial-payment
  // push). Availability is rewards OR offers — the customer screen surfaces a
  // single "Rewards Available" badge for either. The staged amount is the
  // dollar value of whichever redemption is staged (reward/offer are mutually
  // exclusive in posStore, so one chip amount is always 0).
  const setLoyalty = useCartStore((s) => s.setLoyalty)
  const loyaltyAvailable = !!(
    springbig?.customer_enrolled
    && ((springbig.rewards?.length ?? 0) > 0 || (springbig.offers?.length ?? 0) > 0)
  )
  const stagedLoyaltyAmount = rewardChipAmount + offerChipAmount
  useEffect(() => {
    setLoyalty({ available: loyaltyAvailable, stagedAmount: stagedLoyaltyAmount })
  }, [loyaltyAvailable, stagedLoyaltyAmount, setLoyalty])
  // Drop the projection when the customer is removed (this component unmounts)
  // so the next guest sale doesn't inherit the previous customer's badge.
  useEffect(() => () => setLoyalty(null), [setLoyalty])

  const displayName = customer.first_name
    ? `${customer.first_name} ${customer.last_name || ''}`.trim()
    : customer.name || 'Customer'

  const headerNameContent = (
    <p className="text-lg font-semibold text-gray-700 truncate">{displayName}</p>
  )

  return (
    <div className="p-4 border-b border-gray-200">
      <div className="flex items-center justify-between mb-0 gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 max-w-full">
              {onOpenDetails ? (
                <button
                  type="button"
                  onClick={() => onOpenDetails(customer)}
                  className="group flex items-center gap-1.5 text-left min-w-0 cursor-pointer"
                  title="View customer details"
                >
                  {headerNameContent}
                  <Eye
                    size={14}
                    className="text-gray-400 group-hover:text-gray-700 transition-colors shrink-0"
                  />
                </button>
              ) : (
                headerNameContent
              )}
              {hasReward && (
                <button
                  type="button"
                  onClick={() =>
                    onOpenDetails?.(customer, { initialTab: 'loyalty' })
                  }
                  disabled={!onOpenDetails}
                  className="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-100 text-amber-600 hover:bg-amber-200 transition-colors cursor-pointer animate-pulse disabled:cursor-default"
                  title={`${springbig?.rewards?.length} reward${springbig?.rewards?.length === 1 ? '' : 's'} available`}
                >
                  <Gift size={13} />
                </button>
              )}
            </div>
            <div className="space-y-0.5 mt-0.5">
              {customer.email && (
                <div className="flex items-center gap-1.5 text-xs text-gray-400">
                  <Mail size={12} className="flex-shrink-0" />
                  <span className="truncate">{customer.email}</span>
                </div>
              )}
              {customer.phone && (
                <div className="flex items-center gap-1.5 text-xs text-gray-400">
                  <Phone size={12} className="flex-shrink-0" />
                  <span>{customer.phone}</span>
                </div>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={onChangeCustomer}
          disabled={customerLocked}
          title={customerLocked
            ? 'Receipt has payment(s) applied — void the payment in Checkout to change customer'
            : undefined}
          className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-3 py-1.5 rounded-lg transition-colors cursor-pointer shrink-0 disabled:bg-gray-50 disabled:text-gray-300 disabled:cursor-not-allowed"
        >
          Change
        </button>
      </div>
      {/* Badges row: Points, Price Tier, Tags */}
      <div className="flex items-center gap-1.5 text-xs flex-wrap">
        {customer.is_blocked && <BlockedBadge />}
        {customer.points !== undefined && customer.points > 0 && (
          <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium">
            {customer.points} pts
          </span>
        )}
        {customer.price_tier && (
          <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">
            {customer.price_tier}
          </span>
        )}
        {customer.is_medical && (
          <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium">
            Medical
          </span>
        )}
        {customer.tags?.map((tag) => (
          <span
            key={tag}
            className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-medium"
          >
            {tag}
          </span>
        ))}
      </div>

      {/* Staged SpringBig redemption — shows the reward/offer the cashier
          picked from the loyalty drawer. Cleared automatically after a
          successful pay; the X here lets the cashier drop it manually. */}
      {(pendingReward || pendingOffer) && (
        <div className="mt-2 flex items-center gap-1.5 text-xs flex-wrap">
          {pendingReward && (
            <PendingChip
              label={pendingReward.label}
              amount={rewardChipAmount}
              kind="Reward"
              onClear={() => setPendingReward(null)}
            />
          )}
          {pendingOffer && (
            <PendingChip
              label={pendingOffer.label}
              amount={offerChipAmount}
              kind="Offer"
              onClear={() => setPendingOffer(null)}
            />
          )}
        </div>
      )}
    </div>
  )
}

// Render-time dollar resolution for the staged redemption chip. Percent
// redemptions carry the raw percent on `rawAmount` and 0 on `amount`;
// derive the dollar against the live cart subtotal so the chip reflects
// the real reduction the BE will apply.
function stagedDisplayDollar(
  staged: { amount: number; discountType?: string; rawAmount?: number } | null | undefined,
  subtotal: number,
): number {
  if (!staged) return 0
  if (staged.discountType === SPRINGBIG_PERCENT_TYPE) {
    return computePercentDollar(staged.rawAmount ?? 0, subtotal)
  }
  return staged.amount
}

function PendingChip({
  label,
  amount,
  kind,
  onClear,
}: {
  label: string
  amount: number
  kind: 'Reward' | 'Offer'
  onClear: () => void
}) {
  return (
    <span className="inline-flex items-center gap-1 bg-green-50 border border-green-200 text-green-800 pl-1.5 pr-1 py-0.5 rounded font-medium max-w-full">
      <Gift size={12} className="shrink-0" />
      <span className="truncate">
        {kind}: {label} − ${formatPrice(amount)}
      </span>
      <button
        type="button"
        onClick={onClear}
        className="ml-0.5 p-0.5 hover:bg-green-100 rounded shrink-0 cursor-pointer"
        title="Clear staged redemption"
      >
        <CloseIcon size={10} />
      </button>
    </span>
  )
}
